import { gql, useMutation } from "@apollo/client";
import { Switcher } from "components/base/Form";
import { Alert } from "components/base/Toast";
import { formatInvoiceNumbers } from "pages/shipment/formatInvoiceNumbers";
import { Link } from "react-router-dom";
import { useState } from "react";

const UPDATE_SHIPMENT_TRACK_LEADTIME = gql`
  mutation UPDATE_SHIPMENT_TRACK_LEADTIME($shipmentInput: ShipmentInput!) {
    updateShipment(shipmentInput: $shipmentInput) {
      shipment {
        id
        trackLeadtime
      }
    }
  }
`;

const ShipmentName = ({ shipment, className }) => {
  const invoiceNumbers = formatInvoiceNumbers(shipment.shipmentInvoices);

  return (
    <>
      {invoiceNumbers && (
        <Link
          className={className}
          to={`/shipment/shipments/${shipment.id}`}
          target="_blank"
        >
          {invoiceNumbers}
        </Link>
      )}
      <span className={invoiceNumbers ? "ml-3" : ""}>
        {shipment.customerName}
      </span>
    </>
  );
};

const ReportDetails = ({ shipments, type, onTrackLeadtimeChange }) => {
  const [updateShipment, { loading }] = useMutation(
    UPDATE_SHIPMENT_TRACK_LEADTIME,
  );
  const [updatingId, setUpdatingId] = useState(null);

  async function changeTrackLeadtime(shipment) {
    setUpdatingId(shipment.id);
    try {
      await updateShipment({
        variables: {
          shipmentInput: {
            id: shipment.id,
            trackLeadtime: !shipment.track_leadtime,
          },
        },
      });
      await onTrackLeadtimeChange(shipment);
      Alert("success", "Updated.");
    } catch (error) {
      Alert("error", error.message);
    } finally {
      setUpdatingId(null);
    }
  }

  if (type === "ON_TIME_DELIVERY") {
    return (
      <div className="text-sm whitespace-nowrap -mx-2">
        <table>
          <thead>
            <tr className="">
              <th>Shipment</th>
              <th className="text-right">Request Cargo Ready</th>
              <th className="text-right">Delivery to Port</th>
              <th className="text-right">Days Late</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {shipments.map((i, index) => {
              return (
                <tr
                  key={index}
                  className={
                    !i.is_included
                      ? "bg-gray-50 text-gray-500 opacity-50"
                      : i.diff_days > 7
                        ? "bg-red-100"
                        : ""
                  }
                >
                  <td className=" whitespace-normal">
                    <ShipmentName shipment={i} />
                  </td>
                  <td className="text-right">{i.request_cargo_ready_date}</td>
                  <td className="text-right">{i.delivery_to_port_date}</td>
                  <td className="text-right">{i.diff_days}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  if (type === "FULL_CONTAINER_LOAD")
    return (
      <div className="text-sm whitespace-nowrap -mx-2">
        <table>
          <thead>
            <tr className="">
              <th>Shipment</th>
              <th className="text-right">Container</th>
              <th className="text-right">Total CBM</th>
              <th className="text-right">Full Container Load</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {shipments.map((i, index) => {
              return (
                <tr key={index} className={i.diff_days > 7 ? "bg-red-100" : ""}>
                  <td className=" whitespace-normal">
                    <ShipmentName shipment={i} />
                  </td>
                  <td className="text-right">
                    {i.container}({i.container_size})
                  </td>
                  <td className="text-right">{i.total_cbm}</td>
                  <td className="text-right">
                    {(i.full_container_load * 100).toFixed(2)} %
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );

  if (type === "AVE_PRODUCTION_DAYS") {
    const sortedShipments = [...shipments].sort(
      (a, b) => b.production_days - a.production_days,
    );

    return (
      <div className="text-sm whitespace-nowrap -mx-2">
        <table>
          <thead>
            <tr>
              <th>Shipment</th>
              <th className="text-right">Production Days</th>
              <th className="text-right">Track Leadtime</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {sortedShipments.map((shipment) => {
              const isIncluded = shipment.track_leadtime;
              return (
                <tr
                  key={shipment.id}
                  className={
                    isIncluded ? "" : "bg-gray-50 text-gray-500 opacity-50"
                  }
                >
                  <td className="whitespace-normal">
                    <ShipmentName
                      className={isIncluded ? "" : "text-gray-500"}
                      shipment={shipment}
                    />
                  </td>
                  <td className="text-right">{shipment.production_days}</td>
                  <td>
                    <div className="flex justify-end">
                      <Switcher
                        disabled={loading || updatingId !== null}
                        isOn={isIncluded}
                        loading={updatingId === shipment.id}
                        onChange={() => changeTrackLeadtime(shipment)}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  if (type === "AVE_DELAY_DAYS")
    return (
      <div className="text-sm whitespace-nowrap -mx-2">
        <table>
          <thead>
            <tr className="">
              <th>Shipment</th>
              <th className="text-right">Delay Days</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {shipments.map((i, index) => {
              return (
                <tr key={index}>
                  <td className=" whitespace-normal">
                    <ShipmentName shipment={i} />
                  </td>
                  <td className="text-right">{i.delay_days}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );

  if (type === "DELIVERED_TO_PORT") {
    return (
      <div className="text-sm whitespace-nowrap -mx-2">
        <table>
          <thead>
            <tr className="">
              <th>Shipment</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {shipments.map((i, index) => {
              return (
                <tr key={index} className={i.diff_days > 7 ? "bg-red-100" : ""}>
                  <td className=" whitespace-normal">
                    <ShipmentName shipment={i} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  return null;
};

export default ReportDetails;
